import React, { FC, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { PopupFullWidthOnMobile } from '@components/molecules/PopupFullWidthOnMobile';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { OrderLog } from '@types';
import { formatDate, formatTime } from '@utils/format';

interface OrderHistoryModalProps {
    logId: string | null;
    logs: OrderLog[];
    show: boolean;
    onClose: () => void;
}

export const OrderHistoryModal: FC<OrderHistoryModalProps> = ({ logId, logs, show, onClose }) => {
    const { t } = useTranslation();

    const translations = {
        title: t('view-history-modal-component.title'),
        dateLabel: t('view-history-modal-component.date.label'),
        notesLabel: t('view-history-modal-component.notes.label'),
        amountLabel: t('view-history-modal-component.amount.label'),
        storeLabel: t('view-history-modal-component.store.label'),
    };

    const log = logs.find((log) => log.id === logId);

    const [logsToShow, setLogsToShow] = useState<OrderLog[]>(log ? [log] : logs);

    // when logId changes, update values for modal
    useEffect(() => {
        show && setLogsToShow(log ? [log] : logs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    return (
        <PopupFullWidthOnMobile title={translations.title} show={show} onHide={() => onClose()}>
            <>
                {logsToShow.map((item, index) => (
                    <div key={`log-entry-${index}`}>
                        {index !== 0 && <hr />}
                        <div className="row">
                            {/* date + time */}
                            <div className="col-5">
                                <Typography color="muted" fontSize="14">
                                    {translations.dateLabel}
                                </Typography>
                            </div>
                            <div className="col-7 mb-2">
                                <Typography fontSize="16">
                                    <>
                                        {formatDate(item.date)}
                                        <Typography classes="ms-3" color="muted" tagVariant={TypographyTagVariants.span}>
                                            <>({formatTime(item.date)})</>
                                        </Typography>
                                    </>
                                </Typography>
                            </div>

                            {/* notes */}
                            <div className="col-5">
                                <Typography color="muted" fontSize="14">
                                    {translations.notesLabel}
                                </Typography>
                            </div>
                            <div className="col-7">
                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.notes) }}></div>
                                </Typography>
                            </div>

                            {/* amount */}
                            {item.amount !== '' && (
                                <>
                                    <div className="col-5">
                                        <Typography color="muted" fontSize="14">
                                            {translations.amountLabel}
                                        </Typography>
                                    </div>
                                    <div className="col-7">
                                        <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                            {item.amount}
                                        </Typography>
                                    </div>
                                </>
                            )}

                            {/* store */}
                            {item.store && (
                                <>
                                    <div className="col-5">
                                        <Typography color="muted" fontSize="14">
                                            {translations.storeLabel}
                                        </Typography>
                                    </div>
                                    <div className="col-7">
                                        <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                            {item.store}
                                        </Typography>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </>
        </PopupFullWidthOnMobile>
    );
};
